import { ReactNode, useState, useRef, useLayoutEffect } from 'react';
// @mui
import { alpha } from '@mui/material/styles';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Label from 'src/components/label';

/**
 * Important Note:
 * "centered" and "scrollable" variant cannot be used together in MUI Tabs.
 * 
 * When tabs overflow, "scrollable" variant is enabled and "centered" is disabled
 * to show horizontal scrollbar and enable scrolling.
 * 
 * When tabs fit without overflow and centering is requested, 
 * "centered" is enabled with "standard" variant to visually center tabs.
 * 
 * This ensures tabs scroll only when necessary and stay centered otherwise.
 */

export interface TabItem {
  children: ReactNode;
  label: string;
  count?: number;
  className?: string
}
interface ICasTabs {
  value: string;
  handleTabchange: (_: React.SyntheticEvent, newValue: any) => void;
  tabList: TabItem[];
  className?: string;
  centered?: boolean;
}

const CasTabs = ({ value, handleTabchange, tabList, className, centered = false }: ICasTabs) => {
  const tabsRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useLayoutEffect(() => {
    function checkOverflow() {
      if (!tabsRef.current) return;
      const scrollContainer = tabsRef.current.querySelector('.MuiTabs-flexContainer');
      if (!scrollContainer) {
        setIsOverflowing(false);
        return;
      }
      const { scrollWidth, clientWidth } = scrollContainer;
      setIsOverflowing(scrollWidth > clientWidth);
    }
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [tabList, value]);

  return (
  <Tabs
    ref={tabsRef}
    value={value}
    onChange={handleTabchange}
    sx={{
      px: 2.5,
      mb: 1,
      boxShadow: (theme) => `inset 0 -2px 0 0 ${alpha(theme.palette.grey[500], 0.08)}`,
    }}
    className={className}
    variant={isOverflowing ? 'scrollable' : 'standard'}
    centered={centered && !isOverflowing}
  >
    {tabList.map((tab) => {
      if (tab.count)
        return (
          <Tab
            key={tab.label}
            value={tab.label}
            label={tab.label}
            iconPosition="end"
            icon={<Label color="warning">{tab.count}</Label>}
            className={tab.className}
          />
        );
      return <Tab key={tab.label} value={tab.label} label={tab.label} className={tab.className} />;
    })}
  </Tabs>
)};

export default CasTabs;
